import { api, ApiError } from "./api";
import type { LeoDemandeRequest, LeoDemandeResponse, LeoInstantaneResponse } from "@shared/types";

// Léo : chiffres du jour et questions en langage naturel (/api/leo), réponses
// du modèle diffusées au fil de l'eau (/api/leo/flux), voix ElevenLabs lue
// phrase par phrase (/api/leo/parole, repli sur la voix du navigateur), dictée
// par la reconnaissance vocale du navigateur, et niveaux audio pour l'onde.

const BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:3001";

export const getInstantane = (force = false) => api<LeoInstantaneResponse>(`/api/leo/instantane?lang=fr${force ? "&force=1" : ""}`);
export const demander = (body: LeoDemandeRequest) =>
  api<LeoDemandeResponse>("/api/leo/demande", { method: "POST", body: JSON.stringify({ ...body, lang: "fr" }) });

// ---- Flux d'événements (réponse au fil de l'eau) ----

export interface FluxHandlers {
  onDebut?: (meta: { famille: string | null }) => void;
  onDelta: (texte: string) => void;
  onFin: (res: LeoDemandeResponse) => void;
}

/** Envoie la question et relaie les événements `debut`, `delta`, `fin` ; rejette sur erreur HTTP ou `erreur`. */
export async function demanderFlux(body: LeoDemandeRequest, h: FluxHandlers, signal?: AbortSignal): Promise<void> {
  const res = await fetch(`${BASE}/api/leo/flux`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
    body: JSON.stringify({ ...body, lang: "fr" }),
    signal,
  });
  if (!res.ok || !res.body) throw new ApiError(res.status, "flux_error");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let tampon = "";
  let fini = false;
  const traiter = (bloc: string) => {
    let evenement = "message";
    let data = "";
    for (const ligne of bloc.split("\n")) {
      if (ligne.startsWith("event:")) evenement = ligne.slice(6).trim();
      else if (ligne.startsWith("data:")) data += ligne.slice(5).trim();
    }
    if (!data) return;
    const j = JSON.parse(data) as unknown;
    if (evenement === "debut") h.onDebut?.(j as { famille: string | null });
    else if (evenement === "delta") h.onDelta((j as { texte: string }).texte);
    else if (evenement === "fin") {
      fini = true;
      h.onFin(j as LeoDemandeResponse);
    } else if (evenement === "erreur") throw new ApiError(500, "flux_error");
  };
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    tampon += dec.decode(value, { stream: true });
    let i: number;
    while ((i = tampon.indexOf("\n\n")) >= 0) {
      const bloc = tampon.slice(0, i);
      tampon = tampon.slice(i + 2);
      if (bloc.trim()) traiter(bloc);
    }
  }
  if (tampon.trim()) traiter(tampon);
  if (!fini) throw new ApiError(502, "flux_incomplet");
}

// ---- Dictée ----

interface RecognitionResultLike {
  isFinal: boolean;
  0: { transcript: string };
}
interface RecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<RecognitionResultLike>;
}
interface RecognitionLike {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  maxAlternatives: number;
  onstart: (() => void) | null;
  onresult: ((ev: RecognitionEventLike) => void) | null;
  onerror: ((ev: { error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}
type RecognitionCtor = new () => RecognitionLike;

function recognitionCtor(): RecognitionCtor | null {
  const w = window as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export const supportsRecognition = () => recognitionCtor() !== null;

export interface RecognitionHandlers {
  onStart: () => void;
  onInterim: (text: string) => void;
  onFinal: (text: string) => void;
  /** Fin de l'écoute ; `silence` = rien n'a été entendu ; `abandon` = interrompue par nous (pas un vrai silence). */
  onEnd: (silence: boolean, abandon: boolean) => void;
  onDenied: () => void;
  /** Erreur signalée par le navigateur (`network`, `audio-capture`, `no-speech`…), sauf `aborted`. */
  onError?: (code: string) => void;
}

export interface Dictee {
  /** Interrompt la dictée tout de suite (rien n'est envoyé) ; `onEnd(true, true)` est appelé sur-le-champ. */
  abort(): void;
}

interface SessionDictee {
  fini: boolean;
  terminer(silence: boolean, abandon: boolean): void;
}

// Une seule instance de reconnaissance pour toute la page, réutilisée d'une
// dictée à l'autre (Safari se perd avec des instances multiples), et un départ
// qui attend la fin confirmée de la précédente : stop()/abort() ne rendent la
// main qu'au prochain `onend`, et repartir avant était ignoré ou perdu.
let instance: RecognitionLike | null = null;
let instanceActive = false;
let courante: SessionDictee | null = null;
const DELAI_FIN_MS = 700;

/** Démarre une dictée (une phrase) ; renvoie la poignée d'abandon, ou null si indisponible. */
export function startRecognition(h: RecognitionHandlers): Dictee | null {
  const Ctor = recognitionCtor();
  if (!Ctor) return null;
  // La précédente n'est pas finie : on la clôt proprement pour son propriétaire.
  courante?.terminer(true, true);

  let finalText = "";
  let interim = "";
  const s: SessionDictee = {
    fini: false,
    terminer(silence, abandon) {
      if (s.fini) return;
      s.fini = true;
      if (courante === s) courante = null;
      h.onEnd(silence, abandon);
    },
  };
  courante = s;

  const brancher = (rec: RecognitionLike) => {
    rec.lang = "fr-CA";
    rec.interimResults = true;
    rec.continuous = false;
    rec.maxAlternatives = 1;
    rec.onstart = () => {
      if (!s.fini) h.onStart();
    };
    rec.onresult = (ev) => {
      if (s.fini) return;
      interim = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const r = ev.results[i]!;
        if (r.isFinal) finalText += r[0].transcript;
        else interim += r[0].transcript;
      }
      h.onInterim(finalText || interim);
    };
    rec.onerror = (ev) => {
      if (s.fini) return;
      if (ev.error === "not-allowed" || ev.error === "service-not-allowed") h.onDenied();
      else if (ev.error !== "aborted") h.onError?.(ev.error);
    };
    rec.onend = () => {
      instanceActive = false;
      if (s.fini) return;
      const text = (finalText || interim).trim();
      if (text) h.onFinal(text);
      s.terminer(!text, false);
    };
  };

  const demarrer = () => {
    if (s.fini) return;
    const essayer = (rec: RecognitionLike): boolean => {
      brancher(rec);
      try {
        instanceActive = true;
        rec.start();
        return true;
      } catch {
        instanceActive = false;
        return false;
      }
    };
    if (!instance) instance = new Ctor();
    if (essayer(instance)) return;
    // « already started » ou instance cassée : on repart d'une instance neuve.
    try {
      instance.abort();
    } catch {
      // rien
    }
    instance = new Ctor();
    if (!essayer(instance)) s.terminer(true, false);
  };

  if (instanceActive && instance) {
    // La précédente n'a pas encore rendu la main : on l'interrompt et on part sur son `onend` (ou après un court délai).
    const ancienne = instance;
    let lance = false;
    const partir = () => {
      if (lance) return;
      lance = true;
      demarrer();
    };
    ancienne.onend = () => {
      instanceActive = false;
      partir();
    };
    window.setTimeout(() => {
      if (lance) return;
      instanceActive = false;
      instance = null;
      partir();
    }, DELAI_FIN_MS);
    try {
      ancienne.abort();
    } catch {
      partir();
    }
  } else demarrer();

  return {
    abort() {
      if (s.fini) return;
      try {
        instance?.abort();
      } catch {
        // déjà arrêtée
      }
      s.terminer(true, true);
    },
  };
}

// ---- Audio : lecteur unique débloqué au geste, contexte pour l'onde (hors iOS) ----

/** iPhone et iPad (Safari comme Chrome iOS : même moteur, mêmes règles audio). */
export function estIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}
/** Téléphone ou tablette : le micro n'y est pas partageable entre la dictée et une capture Web Audio. */
export function estMobile(): boolean {
  if (typeof navigator === "undefined") return false;
  return estIOS() || /Android/i.test(navigator.userAgent);
}

let ctx: AudioContext | null = null;
/**
 * Contexte Web Audio partagé, pour analyser (niveaux du micro, de la voix).
 * Sur mobile il ne sert qu'à l'entrée : la sortie reste sur l'élément audio
 * (Web Audio y est coupé par l'interrupteur « silencieux »).
 */
function contexteAudio(): AudioContext | null {
  try {
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    if (!ctx) ctx = new AC();
    return ctx;
  } catch {
    return null;
  }
}

const synth = typeof window !== "undefined" ? window.speechSynthesis : undefined;
const SILENCE_MP3 =
  "data:audio/mpeg;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQxAADB8AhSmxhIIEVCSiJrDCQBTcu3UrAIwUdkRgQbFAZC1CQEwTJ9mjRvBA4UOLD8nKVOWfh+UlK3z/177OXrfOdKl7pyn3Xf//WreyTRUoAWgBgkOAGbZHBgG1OF6zM82DWbZaUmMBptgQhGjsyYqc9ae9XFz280948NMBWInluyfk4kCjYkFmwKHDGIVIcGwZIk7XSDnCkG0B6bJ2jGNoIkk8KA0AoN4ITCcZgA==";

// Un seul élément audio pour toute la session : iOS n'autorise la lecture que
// sur un élément déjà joué pendant un geste. On le « réveille » au premier
// clic avec un mp3 muet, puis on change seulement sa source à chaque phrase.
let lecteur: HTMLAudioElement | null = null;
let lecteurDebloque = false;
let synthDebloquee = false;
let analyseurLecteur: AnalyserNode | null = null;

function obtenirLecteur(): HTMLAudioElement {
  if (!lecteur) {
    lecteur = new Audio();
    lecteur.preload = "auto";
    lecteur.setAttribute("playsinline", "");
    (lecteur as HTMLAudioElement & { playsInline?: boolean }).playsInline = true;
  }
  return lecteur;
}

/**
 * Rend le lecteur « vide » entre deux lectures : sans média chargé, WebKit
 * referme la session audio de lecture, sinon la dictée qui suit sur iOS
 * démarre mais n'entend rien (la session reste en mode lecture).
 */
function relacherLecteur(): void {
  if (!lecteur) return;
  try {
    lecteur.onended = null;
    lecteur.onerror = null;
    lecteur.pause();
    lecteur.removeAttribute("src");
    lecteur.load();
  } catch {
    // rien à relâcher
  }
}

/** À appeler pendant un geste (clic, touche) : débloque le lecteur, le contexte et la synthèse. Sans effet une fois fait. */
export function debloquerAudio(): void {
  const c = contexteAudio();
  if (c && c.state !== "running") void c.resume().catch(() => undefined);
  if (!lecteurDebloque) {
    try {
      const a = obtenirLecteur();
      if (a.paused || a.ended) {
        a.src = SILENCE_MP3;
        a.play()
          .then(() => {
            lecteurDebloque = true;
          })
          .catch(() => undefined);
      }
    } catch {
      // pas d'audio
    }
  }
  if (!synthDebloquee && synth) {
    try {
      const u = new SpeechSynthesisUtterance(" ");
      u.volume = 0;
      synth.speak(u);
      synthDebloquee = true;
    } catch {
      // pas de synthèse
    }
  }
}

/** Analyseur branché une seule fois sur le lecteur (hors mobile, contexte actif) ; null sinon. */
function analyseurDuLecteur(a: HTMLAudioElement): AnalyserNode | null {
  if (analyseurLecteur) return analyseurLecteur;
  if (estMobile()) return null;
  const c = contexteAudio();
  if (!c || c.state !== "running") return null;
  try {
    const src = c.createMediaElementSource(a);
    const an = c.createAnalyser();
    an.fftSize = 512;
    src.connect(an);
    an.connect(c.destination);
    analyseurLecteur = an;
    return an;
  } catch {
    return null;
  }
}

function rms(an: AnalyserNode, buf: Uint8Array<ArrayBuffer>, gain: number): number {
  an.getByteTimeDomainData(buf);
  let somme = 0;
  for (let i = 0; i < buf.length; i++) {
    const v = (buf[i]! - 128) / 128;
    somme += v * v;
  }
  return Math.min(1, Math.sqrt(somme / buf.length) * gain);
}

// Niveau « de synthèse » quand on n'a pas d'analyse (iOS) : une respiration
// douce, relevée par les impulsions (mots entendus).
let impulsion = 0;
/** À appeler quand la dictée avance (mot entendu) : l'onde réagit même sans analyse du micro. */
export function impulsionMicro(): void {
  impulsion = 0.7;
}
function niveauSynthetique(t: number, parole: boolean): number {
  const base = parole ? 0.24 + 0.2 * Math.abs(Math.sin(t * 6.3)) + 0.16 * Math.abs(Math.sin(t * 2.1 + 1)) : 0.06 + 0.05 * Math.abs(Math.sin(t * 1.4));
  const n = Math.min(1, base + impulsion);
  impulsion *= 0.93;
  return n;
}

/** Niveau du micro (0..1) pendant la dictée ; renvoie la fonction d'arrêt. */
export function ecouterNiveauMicro(cb: (niveau: number) => void): () => void {
  let arrete = false;
  let flux: MediaStream | null = null;
  let raf = 0;
  const c = contexteAudio();
  const t0 = performance.now();
  const synthetique = () => {
    if (arrete) return;
    cb(niveauSynthetique((performance.now() - t0) / 1000, false));
    raf = requestAnimationFrame(synthetique);
  };
  // Mobile : ouvrir le micro en plus de la dictée du navigateur la rend sourde
  // (Android) ou bascule le son vers l'écouteur (iOS). Onde de synthèse.
  if (!c || estMobile() || !navigator.mediaDevices?.getUserMedia) {
    synthetique();
    return () => {
      arrete = true;
      cancelAnimationFrame(raf);
      cb(0);
    };
  }
  navigator.mediaDevices
    .getUserMedia({ audio: true })
    .then((s) => {
      if (arrete) {
        s.getTracks().forEach((t) => t.stop());
        return;
      }
      flux = s;
      const src = c.createMediaStreamSource(s);
      const an = c.createAnalyser();
      an.fftSize = 512;
      src.connect(an);
      const buf = new Uint8Array(an.fftSize);
      const tick = () => {
        if (arrete) return;
        cb(rms(an, buf, 4));
        raf = requestAnimationFrame(tick);
      };
      tick();
    })
    .catch(() => synthetique());
  return () => {
    arrete = true;
    cancelAnimationFrame(raf);
    flux?.getTracks().forEach((t) => t.stop());
    cb(0);
  };
}

// ---- Écoute par le serveur : micro enregistré, fin de phrase détectée ici, transcription ElevenLabs ----
// Remplace la dictée du navigateur quand le serveur a la clé : fiable sur iPhone
// (la dictée Safari démarre sourde après une lecture audio) et meilleure en
// français québécois. Pas de texte au fil des mots : l'onde suit le micro.

export interface EcouteHandlers extends RecognitionHandlers {
  /** Niveau du micro (0..1) pour l'orbe. */
  onNiveau?: (niveau: number) => void;
  /** L'enregistrement est fini, la transcription est en cours. */
  onTranscription?: () => void;
}

const ECOUTE = {
  calibrageMs: 300, // mesure du bruit de fond au départ
  seuilParole: 0.02, // RMS minimal pour « quelqu'un parle »
  finSilenceMs: 900, // silence après la parole = fin de phrase
  sansParoleMs: 7000, // personne ne parle : on arrête
  maxMs: 25_000,
  sansAnalyseMs: 7000, // pas de niveaux (contexte audio bloqué) : enregistrement à durée fixe
};

function mimeEnregistrement(): string | undefined {
  for (const m of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"]) if (MediaRecorder.isTypeSupported(m)) return m;
  return undefined;
}

export const supportsEcouteServeur = (): boolean => typeof MediaRecorder !== "undefined" && !!navigator.mediaDevices?.getUserMedia;

// Passe à false si le serveur répond 503 (clé absente) : on retombe sur la dictée du navigateur.
let ecouteServeurPossible = true;
export const ecouteServeurDisponible = (): boolean => ecouteServeurPossible && supportsEcouteServeur();

export function startEcouteServeur(h: EcouteHandlers): Dictee | null {
  if (!supportsEcouteServeur()) return null;
  const mime = mimeEnregistrement();
  let fini = false;
  let aParle = false;
  let flux: MediaStream | null = null;
  let rec: MediaRecorder | null = null;
  let raf = 0;
  let garde = 0;
  const morceaux: Blob[] = [];

  const lacher = () => {
    cancelAnimationFrame(raf);
    window.clearTimeout(garde);
    flux?.getTracks().forEach((t) => t.stop());
    flux = null;
    h.onNiveau?.(0);
  };
  const terminer = (silence: boolean, abandon: boolean) => {
    if (fini) return;
    fini = true;
    lacher();
    h.onEnd(silence, abandon);
  };
  // Fin de phrase : on arrête l'enregistreur, la transcription suit dans `onstop`.
  const arreter = () => {
    cancelAnimationFrame(raf);
    window.clearTimeout(garde);
    if (rec && rec.state !== "inactive") {
      try {
        rec.stop();
        return;
      } catch {
        // enregistreur cassé : on finit sans texte
      }
    }
    terminer(true, false);
  };
  const transcrire = async () => {
    if (fini) return;
    lacher();
    const blob = new Blob(morceaux, { type: mime ?? "application/octet-stream" });
    if (!aParle || blob.size < 2000) {
      terminer(true, false);
      return;
    }
    h.onTranscription?.();
    try {
      const res = await fetch(`${BASE}/api/leo/transcrire`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": blob.type || "application/octet-stream" },
        body: blob,
      });
      if (fini) return;
      if (res.status === 503) {
        ecouteServeurPossible = false;
        h.onError?.("transcription");
        terminer(true, false);
        return;
      }
      if (!res.ok) {
        h.onError?.(res.status >= 500 ? "transcription" : "network");
        terminer(true, false);
        return;
      }
      const j = (await res.json()) as { texte?: string };
      const texte = (j.texte ?? "").trim();
      if (fini) return;
      if (texte) h.onFinal(texte);
      terminer(!texte, false);
    } catch {
      if (fini) return;
      h.onError?.("network");
      terminer(true, false);
    }
  };

  navigator.mediaDevices
    .getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } })
    .then((s) => {
      if (fini) {
        s.getTracks().forEach((t) => t.stop());
        return;
      }
      flux = s;
      try {
        rec = mime ? new MediaRecorder(s, { mimeType: mime }) : new MediaRecorder(s);
        rec.ondataavailable = (ev) => {
          if (ev.data.size) morceaux.push(ev.data);
        };
        rec.onstop = () => void transcrire();
        rec.onerror = () => {
          h.onError?.("audio-capture");
          terminer(true, false);
        };
        rec.start();
      } catch {
        h.onError?.("audio-capture");
        terminer(true, false);
        return;
      }
      h.onStart();
      garde = window.setTimeout(arreter, ECOUTE.maxMs);

      // Fin de phrase au niveau sonore : bruit de fond mesuré au départ, parole
      // au-dessus du seuil, puis un silence franc. Sans analyse, durée fixe.
      const c = contexteAudio();
      const t0 = performance.now();
      if (!c) {
        aParle = true;
        garde = window.setTimeout(arreter, ECOUTE.sansAnalyseMs);
        return;
      }
      if (c.state !== "running") void c.resume().catch(() => undefined);
      let an: AnalyserNode;
      try {
        const src = c.createMediaStreamSource(s);
        an = c.createAnalyser();
        an.fftSize = 1024;
        src.connect(an);
      } catch {
        aParle = true;
        garde = window.setTimeout(arreter, ECOUTE.sansAnalyseMs);
        return;
      }
      const buf = new Uint8Array(an.fftSize);
      let plancher = 0;
      let echantillons = 0;
      let dernierSon = t0;
      let dureeFixe = false;
      const tick = () => {
        if (fini) return;
        const t = performance.now();
        if (c.state !== "running") {
          // Contexte bloqué (iOS sans geste) : on n'aura pas de niveaux.
          if (t - t0 > 800 && !dureeFixe) {
            dureeFixe = true;
            aParle = true;
            window.clearTimeout(garde);
            garde = window.setTimeout(arreter, ECOUTE.sansAnalyseMs);
          }
          raf = requestAnimationFrame(tick);
          return;
        }
        const v = rms(an, buf, 1);
        if (t - t0 < ECOUTE.calibrageMs) {
          plancher = (plancher * echantillons + v) / (echantillons + 1);
          echantillons++;
        }
        const seuil = Math.max(ECOUTE.seuilParole, plancher * 2.5);
        if (v > seuil) {
          aParle = true;
          dernierSon = t;
        }
        h.onNiveau?.(Math.min(1, v * 4));
        if (!dureeFixe) {
          if (aParle && t - dernierSon > ECOUTE.finSilenceMs) return arreter();
          if (!aParle && t - t0 > ECOUTE.sansParoleMs) return arreter();
        }
        raf = requestAnimationFrame(tick);
      };
      tick();
    })
    .catch((err: unknown) => {
      const nom = (err as { name?: string } | null)?.name ?? "";
      if (nom === "NotAllowedError" || nom === "SecurityError") h.onDenied();
      else h.onError?.("audio-capture");
      terminer(true, false);
    });

  return {
    abort() {
      if (fini) return;
      try {
        if (rec && rec.state !== "inactive") rec.stop();
      } catch {
        // déjà arrêté
      }
      terminer(true, true);
    },
  };
}

// ---- Voix de Léo : file de phrases, ElevenLabs d'abord, navigateur sinon ----

function phrases(text: string): string[] {
  const propre = text.replace(/[·•]/g, ", ").replace(/⚠/g, "").replace(/\s+/g, " ").trim();
  const parts = propre.match(/[^.!?…]+[.!?…]+["»)]?(?:\s|$)|[^.!?…]+$/g) ?? [propre];
  const out: string[] = [];
  for (const p of parts) {
    const t = p.trim();
    if (!t) continue;
    if (t.length <= 240) out.push(t);
    else for (const m of t.match(/.{1,240}(?:[,;:]\s|\s|$)/g) ?? [t]) if (m.trim()) out.push(m.trim());
  }
  return out;
}

/** Découpe les phrases complètes d'un texte en cours de diffusion ; renvoie ce qui reste à lire plus tard. */
export function phrasesCompletes(texte: string): { pretes: string[]; reste: string } {
  const m = texte.match(/^[\s\S]*?[.!?…]+["»)]?(?=\s)/);
  if (!m) return { pretes: [], reste: texte };
  return { pretes: phrases(m[0]), reste: texte.slice(m[0].length) };
}

function pickVoice(): SpeechSynthesisVoice | null {
  if (!synth) return null;
  const voices = synth.getVoices();
  for (const want of ["fr-ca", "fr-fr", "fr"]) {
    const found = voices.filter((v) => v.lang.replace("_", "-").toLowerCase().startsWith(want));
    if (found.length) return found.find((v) => /am[eé]lie|thomas|nicolas|audrey|aurelie|premium|enhanced|natural|neural/i.test(v.name)) ?? found[0] ?? null;
  }
  return null;
}
if (synth) synth.addEventListener?.("voiceschanged", () => void synth.getVoices());

async function chercherParole(texte: string): Promise<Blob | null> {
  const res = await fetch(`${BASE}/api/leo/parole`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "audio/mpeg" },
    body: JSON.stringify({ texte }),
  });
  if (res.status === 503) return null; // pas de voix serveur : navigateur
  if (!res.ok) throw new Error(`parole ${res.status}`);
  return res.blob();
}

export interface VoixHandlers {
  /** Léo commence (true) ou finit (false) de parler. */
  onEtat?: (parle: boolean) => void;
  /** Niveau sonore de la voix (0..1) pour l'onde. */
  onNiveau?: (niveau: number) => void;
}

/** File de lecture : les phrases s'enchaînent dans l'ordre, la suivante est préparée pendant la précédente. */
export class Voix {
  private file: string[] = [];
  private prefetch = new Map<string, Promise<Blob | null>>();
  private enCours = false;
  private generation = 0;
  private serveur = true;
  private raf = 0;
  private urlCourante: string | null = null;
  constructor(private h: VoixHandlers = {}) {}

  get parle(): boolean {
    return this.enCours || this.file.length > 0;
  }

  dire(texte: string): void {
    const ps = phrases(texte);
    if (!ps.length) return;
    for (const p of ps) {
      this.file.push(p);
      if (this.serveur && !this.prefetch.has(p)) this.prefetch.set(p, chercherParole(p).catch(() => null));
    }
    void this.pomper();
  }

  couper(): void {
    this.generation++;
    this.file = [];
    this.prefetch.clear();
    cancelAnimationFrame(this.raf);
    if (this.urlCourante) {
      URL.revokeObjectURL(this.urlCourante);
      this.urlCourante = null;
    }
    relacherLecteur();
    if (synth && (synth.speaking || synth.pending)) synth.cancel();
    if (this.enCours) {
      this.enCours = false;
      this.h.onEtat?.(false);
    }
    this.h.onNiveau?.(0);
  }

  private async pomper(): Promise<void> {
    if (this.enCours) return;
    const p = this.file.shift();
    if (!p) return;
    const gen = this.generation;
    this.enCours = true;
    this.h.onEtat?.(true);
    try {
      let blob: Blob | null = null;
      if (this.serveur) {
        blob = await (this.prefetch.get(p) ?? chercherParole(p).catch(() => null));
        this.prefetch.delete(p);
        if (blob === null) this.serveur = false;
      }
      if (gen !== this.generation) return;
      if (blob) await this.jouer(blob, gen);
      else await this.direNavigateur(p, gen);
    } catch {
      // phrase perdue : on passe à la suivante
    } finally {
      if (gen === this.generation) {
        this.enCours = false;
        if (this.file.length) void this.pomper();
        else {
          relacherLecteur();
          this.h.onEtat?.(false);
          this.h.onNiveau?.(0);
        }
      }
    }
  }

  private jouer(blob: Blob, gen: number): Promise<void> {
    return new Promise((resolve) => {
      const a = obtenirLecteur();
      const url = URL.createObjectURL(blob);
      this.urlCourante = url;
      let fini = false;
      const fin = () => {
        if (fini) return;
        fini = true;
        cancelAnimationFrame(this.raf);
        if (a.onended === fin) a.onended = null;
        if (a.onerror === fin) a.onerror = null;
        if (this.urlCourante === url) {
          URL.revokeObjectURL(url);
          this.urlCourante = null;
        }
        resolve();
      };
      a.onended = fin;
      a.onerror = fin;
      a.src = url;
      this.animerNiveau(a, gen);
      a.play().catch(() => fin());
    });
  }

  /** Onde au rythme de la voix : analyse du signal quand on l'a, respiration de synthèse sinon (iOS). */
  private animerNiveau(a: HTMLAudioElement, gen: number): void {
    cancelAnimationFrame(this.raf);
    const an = analyseurDuLecteur(a);
    const buf = an ? new Uint8Array(an.fftSize) : null;
    const t0 = performance.now();
    const tick = () => {
      if (gen !== this.generation || a.ended) return;
      this.h.onNiveau?.(an && buf ? rms(an, buf, 3) : niveauSynthetique((performance.now() - t0) / 1000, true));
      this.raf = requestAnimationFrame(tick);
    };
    tick();
  }

  private direNavigateur(texte: string, gen: number): Promise<void> {
    return new Promise((resolve) => {
      if (!synth) return resolve();
      const u = new SpeechSynthesisUtterance(texte);
      u.lang = "fr-CA";
      const v = pickVoice();
      if (v) u.voice = v;
      u.rate = 1.1;
      u.onend = () => resolve();
      u.onerror = () => resolve();
      const t0 = performance.now();
      const tick = () => {
        if (gen !== this.generation || !synth.speaking) return;
        this.h.onNiveau?.(niveauSynthetique((performance.now() - t0) / 1000, true));
        this.raf = requestAnimationFrame(tick);
      };
      // Chrome perd une phrase lancée juste après cancel() : courte respiration.
      window.setTimeout(() => {
        if (gen !== this.generation) return resolve();
        synth.speak(u);
        tick();
      }, 60);
    });
  }
}

export const supportsSpeech = () => !!synth || typeof Audio !== "undefined";

/** « 14:05 » en heure de Montréal. */
export function heureCourte(iso: string): string {
  return new Date(iso).toLocaleTimeString("fr-CA", { hour: "2-digit", minute: "2-digit", timeZone: "America/Montreal" });
}
